import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { UploadCloud, RotateCcw, Palette, Image as ImageIcon, X, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Client-side compression: decode image → draw to canvas → re-encode as JPEG
// so a 3 MB PNG becomes a ~80 KB data URL. Keeps Mongo doc size sane.
const MAX_LOGO_DIMENSION = 512;
const compressLogo = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, MAX_LOGO_DIMENSION / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      // PNG preserves transparency; JPEG for photo-heavy logos would be smaller
      // but breaks the 99% case (logos on white/dark backgrounds).
      const dataUrl = canvas.toDataURL('image/png');
      resolve(dataUrl);
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const DEFAULTS = {
  primary_color: '#7C3AED',
  secondary_color: '#4F46E5',
  accent_color: '#10B981',
};

const BrandingSettings = () => {
  const { user, checkAuth } = useAuth();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef(null);

  const featureEnabled = (user?.plan_features || []).includes('custom_branding');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/company/branding`);
        if (mounted) setBranding(res.data);
      } catch (e) {
        if (mounted) setBranding({ ...DEFAULTS, logo_data_url: null, feature_enabled: featureEnabled });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const setField = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Sadece görsel dosyalar yükleyebilirsiniz');
      return;
    }
    try {
      const dataUrl = await compressLogo(file);
      // Rough size check (client-side); backend enforces the hard limit.
      if (dataUrl.length > 700_000) {
        toast.error('Logo çok büyük. Lütfen daha küçük bir dosya deneyin.');
        return;
      }
      setField('logo_data_url', dataUrl);
    } catch (err) {
      toast.error('Logo işlenemedi');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeLogo = () => setField('logo_data_url', '');

  const save = async () => {
    if (!branding) return;
    setSaving(true);
    try {
      const payload = {
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
        accent_color: branding.accent_color,
      };
      // Only send logo if it changed (avoid re-uploading huge data URLs).
      if (branding.logo_data_url !== undefined) {
        payload.logo_data_url = branding.logo_data_url || '';
      }
      const res = await axios.put(`${API}/company/branding`, payload);
      setBranding(res.data);
      setDirty(false);
      toast.success('Markalama kaydedildi');
      await checkAuth(); // Refresh user so Dashboard picks up new logo/colors
    } catch (e) {
      const msg = e.response?.data?.detail || 'Kaydedilemedi';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Markalama ayarları platform varsayılanlarına döndürülecek. Devam edilsin mi?')) return;
    setSaving(true);
    try {
      const res = await axios.delete(`${API}/company/branding`);
      setBranding(res.data);
      setDirty(false);
      toast.success('Markalama sıfırlandı');
      await checkAuth();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Sıfırlama başarısız';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8"><div className="animate-pulse text-gray-500">Yükleniyor…</div></div>;
  }

  const b = branding || DEFAULTS;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="branding-settings-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Palette className="w-6 h-6 text-purple-600" />
          Özel Markalama
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Şirket logonuzu ve marka renklerinizi kaydedin — dashboard&apos;da tüm ekiplere anında yansır.
        </p>
      </div>

      {!featureEnabled && (
        <Card className="p-5 mb-6 border-amber-200 bg-amber-50" data-testid="branding-feature-locked">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900">Bu özellik planınıza dahil değil</div>
              <div className="text-sm text-amber-800 mt-1">
                Özel Markalama (logo + marka renkleri) <strong>Professional</strong> ve
                <strong> Enterprise</strong> planlarında bulunur.
                <a href="/subscription" className="ml-2 underline font-semibold">Planları görüntüle →</a>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ----- Left column: form ----- */}
        <div className="space-y-6">
          {/* Logo */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ImageIcon className="w-4 h-4 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Şirket Logosu</h3>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              PNG veya JPG. Max ~500KB. Yatay logolar (ör. 512×128) en iyi sonucu verir.
            </p>
            {b.logo_data_url ? (
              <div className="flex items-center gap-4">
                <div className="w-40 h-20 bg-gray-50 border rounded-lg flex items-center justify-center overflow-hidden">
                  <img src={b.logo_data_url} alt="Company logo" className="max-w-full max-h-full object-contain" />
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={!featureEnabled}
                    data-testid="branding-change-logo"
                  >
                    Değiştir
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={removeLogo}
                    disabled={!featureEnabled}
                    className="text-rose-600"
                    data-testid="branding-remove-logo"
                  >
                    <X className="w-4 h-4 mr-1" />Kaldır
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={!featureEnabled}
                className={`w-full border-2 border-dashed rounded-lg p-6 flex flex-col items-center gap-2 transition-colors ${
                  featureEnabled ? 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/50 cursor-pointer' : 'border-gray-200 opacity-60 cursor-not-allowed'
                }`}
                data-testid="branding-upload-logo"
              >
                <UploadCloud className="w-6 h-6 text-gray-400" />
                <div className="text-sm text-gray-600">Logo dosyası seçin</div>
                <div className="text-xs text-gray-400">Otomatik olarak 512px&apos;e küçültülür</div>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFilePick}
              data-testid="branding-logo-input"
            />
          </Card>

          {/* Colors */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Palette className="w-4 h-4 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Marka Renkleri</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { key: 'primary_color', label: 'Birincil', testid: 'branding-color-primary' },
                { key: 'secondary_color', label: 'İkincil', testid: 'branding-color-secondary' },
                { key: 'accent_color', label: 'Vurgu', testid: 'branding-color-accent' },
              ].map((c) => (
                <div key={c.key}>
                  <Label className="text-xs text-gray-600">{c.label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={b[c.key] || DEFAULTS[c.key]}
                      onChange={(e) => setField(c.key, e.target.value)}
                      disabled={!featureEnabled}
                      className="w-10 h-10 border rounded cursor-pointer disabled:opacity-50"
                      data-testid={c.testid}
                    />
                    <Input
                      value={b[c.key] || ''}
                      onChange={(e) => setField(c.key, e.target.value)}
                      placeholder={DEFAULTS[c.key]}
                      disabled={!featureEnabled}
                      className="font-mono text-xs uppercase"
                      data-testid={`${c.testid}-hex`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex gap-3">
            <Button
              onClick={save}
              disabled={!featureEnabled || !dirty || saving}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="branding-save"
            >
              {saving ? 'Kaydediliyor…' : 'Değişiklikleri Kaydet'}
            </Button>
            <Button
              variant="outline"
              onClick={reset}
              disabled={!featureEnabled || saving}
              data-testid="branding-reset"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" />Varsayılana Döndür
            </Button>
          </div>
        </div>

        {/* ----- Right column: live preview ----- */}
        <div>
          <Card className="p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Canlı Önizleme</h3>
            <div
              className="rounded-xl overflow-hidden border shadow-sm"
              style={{ background: `linear-gradient(180deg, ${b.primary_color} 0%, ${b.secondary_color} 100%)` }}
              data-testid="branding-preview"
            >
              {/* Fake dashboard header */}
              <div className="flex items-center justify-between px-4 py-3 bg-white/95">
                <div className="flex items-center gap-2">
                  {b.logo_data_url ? (
                    <img src={b.logo_data_url} alt="preview logo" className="h-8 max-w-[140px] object-contain" />
                  ) : (
                    <span className="text-lg font-bold" style={{ color: b.primary_color }}>
                      {user?.company?.company_name || 'Şirketiniz'}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">Dashboard</span>
              </div>
              {/* Fake KPI card */}
              <div className="p-5 space-y-3">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-xs text-gray-500">Toplam Avans</div>
                  <div className="text-2xl font-bold" style={{ color: b.primary_color }}>₺ 24.500</div>
                  <div className="mt-2 inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold text-white"
                       style={{ background: b.accent_color }}>
                    +12% bu ay
                  </div>
                </div>
                <button
                  type="button"
                  className="px-3 py-2 rounded-md text-sm font-semibold text-white shadow-sm"
                  style={{ background: b.primary_color }}
                >
                  Yeni Avans Talebi
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Renkler ve logo, dashboard&apos;un sidebar başlığında, butonlarda ve gönderilen e-postaların üstünde kullanılır.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BrandingSettings;
